/**
 * What the player SEES - state drives every visual, and each visual is a plugin:
 *   KINDS     - per item kind (backend mechanics/core.py has the same ids): `overlay(it, s, g)` adds
 *               to the item in the scene (the camera's view cone and status light, a meter's dial)
 *   MARKS     - per state prop, on any item: `mark(it, s)` (the amber lamp on something open, the
 *               switch light) and `badge(s)` (🔒 🛡️ ✍️2 😟 in the item's badge pill)
 *   CLOSEUPS  - what opens when an item is clicked: the first whose `match(it, s)` holds -
 *               `View({ it, s, g })` renders it (a log to read and sign, a monitor, the plain view)
 * `g` is the player's API for these views: { spec, ctx, byId, asset, say, attempt, actsFor, aboutOf,
 * partnersOf, draftChips, setDraftChips, setOpen, monitorTime, badges, take (or null) }.
 * Every control is captioned with its action's name - a 🔑 alone told the player nothing (and was
 * sometimes a trap).
 */
import React, { useEffect, useRef, useState } from 'react';
import { createRegistry } from './registry';
import { Document, Highlighted, MECHANIC_VIEWS, Spoken } from './itemMechanics';
import { VISUALS } from './stateVisuals';

// ---------------------------------------------------------------- widgets the views share

export function Dial({ reading, value, size = 90 }) {
  const { min = 0, max = 100, limit } = reading;
  const angle = (v) => -120 + (240 * (Math.min(max, Math.max(min, v)) - min)) / Math.max(1e-6, max - min);
  const [shown, setShown] = useState(min);
  useEffect(() => {
    const t = setTimeout(() => setShown(value), 150);
    return () => clearTimeout(t);
  }, [value]);
  const point = (deg, r) => [50 + r * Math.sin((deg * Math.PI) / 180), 55 - r * Math.cos((deg * Math.PI) / 180)];
  const [lx, ly] = point(angle(limit ?? max), 40);
  const [lx2, ly2] = point(angle(limit ?? max), 30);
  return (
    <svg className="mg-dial" viewBox="0 0 100 80" width={size} height={size * 0.8}>
      <path d="M12 72 A42 42 0 1 1 88 72" fill="#fdf6e3" stroke="#3a2d17" strokeWidth="3" />
      {limit !== undefined && <line x1={lx} y1={ly} x2={lx2} y2={ly2} stroke="#c0392b" strokeWidth="4" />}
      <g className="mg-needle" style={{ transform: `rotate(${angle(shown)}deg)` }}>
        <line x1="50" y1="55" x2="50" y2="18" stroke="#1e1a15" strokeWidth="3" strokeLinecap="round" />
      </g>
      <circle cx="50" cy="55" r="4" fill="#1e1a15" />
    </svg>
  );
}

/** A control with its action's name under it - so the player knows what pressing it DOES. */
export function Captioned({ caption, children }) {
  return (
    <div className="mg-control">
      {children}
      {caption && <span className="mg-caption">{caption}</span>}
    </div>
  );
}

export function HoldButton({ label, onDone, ms = 1300 }) {
  const [holding, setHolding] = useState(false);
  const timer = useRef(null);
  const start = () => { setHolding(true); timer.current = setTimeout(() => { setHolding(false); onDone(); }, ms); };
  const stop = () => { setHolding(false); clearTimeout(timer.current); };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <button type="button" className={`mg-hold ${holding ? 'is-holding' : ''}`} style={{ '--hold-ms': `${ms}ms` }}
      onPointerDown={start} onPointerUp={stop} onPointerLeave={stop}>{label}</button>
  );
}

// ---------------------------------------------------------------- kinds: extras on the item in the scene

export const KINDS = createRegistry('item kind');
KINDS.register({
  id: 'camera',
  overlay: (it, s) => (
    <>
      <span className={`mg-cone ${s.blocked_by ? 'is-blocked' : 'is-clear'}`} />
      <span className={`mg-led ${s.blocked_by ? 'is-red' : 'is-green'}`} />
    </>
  )
});
KINDS.register({
  id: 'meter',
  overlay: (it, s) => it.reading && <span className="mg-meter-face"><Dial reading={it.reading} value={s.value ?? it.reading.value} size={60} /></span>
});
['person', 'record', 'object', 'place'].forEach((id) => KINDS.register({ id }));

// ---------------------------------------------------------------- marks: what a state prop shows on any item

const MOOD_EMOJI = { angry: '😠', happy: '🙂', worried: '😟', nervous: '😬', upset: '😣' };
export const MARKS = createRegistry('state mark');
MARKS.register({ id: 'open', mark: (it, s) => it.kind !== 'person' && s.open === true && <span className="mg-lamp" />, badge: (s) => s.open === true && '↔' });
MARKS.register({ id: 'on', mark: (it, s) => typeof s.on === 'boolean' && <span className={`mg-switch is-${s.on ? 'on' : 'off'}`} /> });
MARKS.register({ id: 'locked', badge: (s) => (s.locked === true && '🔒') || (s.locked === false && '🔓') });
MARKS.register({ id: 'armed', badge: (s) => s.armed === true && '🛡️' });
MARKS.register({ id: 'signatures', badge: (s) => typeof s.signatures === 'number' && `✍️${s.signatures}` });
MARKS.register({ id: 'mood', badge: (s) => MOOD_EMOJI[s.mood] });
// A vessel (backend flows.py inv.mixes): what's in it in this state, drawn as the blend of their colours - so a wrong
// mix shows as the wrong colour. Known liquids have their colour; anything else gets a steady colour from its name.
const LIQUIDS = [[/water|saline/, [168, 216, 240]], [/milk|cream/, [245, 245, 238]], [/coffee|espresso/, [111, 78, 55]],
  [/tea\b/, [181, 101, 29]], [/iodine|betadine/, [201, 122, 28]], [/blood/, [139, 0, 0]], [/juice|orange/, [245, 166, 35]],
  [/oil/, [229, 196, 90]], [/acid/, [215, 245, 66]], [/bleach|alkali/, [240, 248, 208]],
  [/syrup|medicine|amoxicillin|antibiotic|suspension|powder/, [242, 182, 198]]];
export const colourOf = (name) => {
  const text = String(name || '').toLowerCase();
  const known = LIQUIDS.find(([re]) => re.test(text));
  if (known) return known[1];
  const hue = [...text].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  const c = (n) => { const k = (n + hue / 30) % 12; return Math.round(255 * (0.7 - 0.4 * Math.max(-1, Math.min(k - 3, 9 - k, 1)))); };
  return [c(0), c(8), c(4)];
};
export const blendOf = (names) => {
  const cols = names.map(colourOf);
  const avg = [0, 1, 2].map((i) => Math.round(cols.reduce((n, col) => n + col[i], 0) / cols.length));
  return `rgb(${avg.join(', ')})`;
};
MARKS.register({
  id: 'liquid',
  mark: (it, s) => it.inv?.mixes?.[s.stage] && (
    <span className="mg-liquid" title={it.inv.mixes[s.stage].join(' + ')} style={{ '--liquid': blendOf(it.inv.mixes[s.stage]) }} />
  )
});
// An amount (a dose in a cup, a fill in a tank): a level gauge with its reading, on the item itself.
MARKS.register({
  id: 'amount',
  mark: (it, s) => typeof s.amount === 'number' && it.amount && (
    <span className="mg-level" title={`${s.amount} ${it.amount.unit || ''}`}>
      <span className="mg-level-tube"><i style={{ height: `${levelPct(it.amount, s.amount)}%` }} /></span>
      <b>{s.amount}{it.amount.unit ? ` ${it.amount.unit}` : ''}</b>
    </span>
  )
});

function levelPct(meta, value) {
  const lo = Math.min(0, meta.min ?? 0);
  return Math.max(4, Math.min(100, (100 * (value - lo)) / Math.max(1e-6, (meta.max ?? value) - lo)));
}

// What a container holds (a procedure's mixer): one level for everything in it, and what each part is.
MARKS.register({
  id: 'contents',
  mark: (it, s) => it.contents && (() => {
    const parts = it.contents.ingredients.map((x) => ({ ...x, v: s[`contains_${x.id}`] || 0 }));
    const total = parts.reduce((n, x) => n + x.v, 0);
    return (
      <span className="mg-level" title={parts.filter((x) => x.v).map((x) => `${x.v} ${it.contents.unit} ${x.name}`).join(' + ') || 'empty'}>
        <span className="mg-level-tube"><i style={{ height: `${total ? levelPct({ min: 0, max: it.contents.max || total }, total) : 0}%` }} /></span>
        <b>{total ? `${total} ${it.contents.unit}` : 'empty'}</b>
      </span>
    );
  })()
});

// Badge order in the pill (locked, open, armed, signatures, mood) - kept from before the registries.
const BADGE_ORDER = ['locked', 'open', 'armed', 'signatures', 'mood'];
export const badgesFor = (s) => [...BADGE_ORDER.map((id) => MARKS.get(id)), ...MARKS.all().filter((m) => !BADGE_ORDER.includes(m.id))]
  .filter((m) => m && m.badge).map((m) => m.badge(s)).filter(Boolean);

/** Everything the item's kind and its state add to it in the scene (before the badge pill). */
export function ItemExtras({ it, s, g }) {
  const kind = KINDS.get(it.kind);
  return (
    <>
      {kind && kind.overlay && kind.overlay(it, s, g)}
      {MARKS.all().filter((m) => m.mark).map((m) => <React.Fragment key={m.id}>{m.mark(it, s)}</React.Fragment>)}
    </>
  );
}

// ---------------------------------------------------------------- close-ups

const entryText = (e) => (typeof e === 'string' ? { time: '', text: e } : { time: e.time || '', text: e.text || '' });

function LogCloseUp({ it, s, g }) {
  const rows = (s.entries || []).map(entryText);
  const initial = ((it.state || {}).entries || []).length;
  const writes = g.actsFor(it.id, 'write');
  const write = writes[0];
  // One log can take several entries - the thing put on the line says which one is being written.
  const sign = () => {
    const action = writes.find((a) => g.aboutOf(a) && g.partnersOf(g.aboutOf(a)).some((id) => g.draftChips.includes(id))) || writes.find((a) => !g.aboutOf(a));
    if (!action) { g.say(["That's not worth a line in here."]); g.setDraftChips([]); return; }
    g.attempt(action);
    g.setDraftChips([]);
  };
  const chips = g.spec.items.filter((x) => g.ctx.seen.includes(x.id) && x.id !== it.id);
  return (
    <div className="mg-log">
      <h4>{it.name}</h4>
      {rows.map((r, i) => (
        <div key={`${r.time}-${i}`} className={`mg-log-row ${i >= initial ? 'is-added' : ''}`}><span>{r.time}</span><span>{r.text}</span></div>
      ))}
      {write && (
        <>
          <div className="mg-log-row mg-log-blank">
            <span>now</span>
            <span className="mg-slot">{g.draftChips.length ? g.draftChips.map((id) => <em key={id}>{g.byId[id]?.name}</em>) : ' '}</span>
          </div>
          <div className="mg-chips">
            {chips.map((x) => (
              <button key={x.id} type="button" className="mg-chip" title={x.name} disabled={g.draftChips.includes(x.id)}
                onClick={() => g.setDraftChips((c) => [...c, x.id])}>
                {x.src ? <img src={g.asset(x.src)} alt="" /> : '📍'}
              </button>
            ))}
            <button type="button" className="mg-pen" disabled={!g.draftChips.length}
              onClick={sign}>✍️</button>
          </div>
        </>
      )}
    </div>
  );
}

function MonitorCloseUp({ it, s, g }) {
  const blocker = s.blocked_by ? g.byId[s.blocked_by] : null;
  return (
    <div className={`mg-monitor ${blocker ? 'is-blocked' : ''}`}>
      <span className="mg-rec"><i>●</i> REC {it.name}</span>
      <span className="mg-stamp">{g.monitorTime}</span>
      {blocker
        ? (blocker.src ? <img className="mg-monitor-blocker" src={g.asset(blocker.src)} alt="" /> : <div className="mg-monitor-dark" />)
        : <div className="mg-monitor-clear" style={{ backgroundImage: `url(${g.asset(g.spec.background_src)})` }} />}
      <span className="mg-scanlines" />
    </div>
  );
}

function PlainCloseUp({ it, s, g }) {
  const holds = g.actsFor(it.id, 'hold');
  const switches = g.actsFor(it.id, 'switch');
  const dial = it.reading ? { reading: it.reading, value: s.value ?? it.reading.value } : null;
  return (
    <div className="mg-closeup-plain">
      {dial ? (
        <div className="mg-meter-big">
          <Dial reading={dial.reading} value={dial.value} size={240} />
          <div className="mg-meter-value">{dial.value} {dial.reading.unit}</div>
        </div>
      ) : it.src && <img className={s.ringing ? 'is-ringing' : ''} src={g.asset(it.src)} alt={it.name} />}
      <div className="mg-closeup-side">
        {g.badges(s).length > 0 && <div className="mg-closeup-badges">{g.badges(s).join('  ')}</div>}
        <Controls it={it} s={s} g={g} holds={holds} switches={switches} />
      </div>
    </div>
  );
}

/** The item's press-and-hold and switch controls, each captioned, and 🎒 Take it for things you carry. */
function Controls({ it, s, g, holds, switches }) {
  return (
    <>
      {holds.map((a) => (
        <Captioned key={a.id} caption={a.name}><HoldButton label="🔑" onDone={() => { if (g.attempt(a)) g.setOpen(null); }} /></Captioned>
      ))}
      {switches.map((a) => (
        <Captioned key={a.id} caption={a.name}>
          <button type="button" className={`mg-toggle ${s.on || s.armed ? 'is-on' : ''}`}
            onClick={() => { if (g.attempt(a)) g.setOpen(null); }}><span /></button>
        </Captioned>
      ))}
      {g.take && <button type="button" className="mg-take" onClick={() => g.take(it.id)}>🎒 Take it</button>}
    </>
  );
}

/** An amount to measure: the level in a graduated tube, its reading, and − / + to pour less or more.
 * Each step is one of the mission's `adjust` actions (one per amount the player can set); what it sets is
 * the action's effect - the item's own `amount` (correct a cup), or another item's (pour this bottle INTO
 * the mixer: the mixer's `contains_<bottle>`). */
function MeasureCloseUp({ it, s, g }) {
  const steps = g.actsFor(it.id, 'adjust').slice().sort((a, b) => a.value - b.value);
  const target = steps[0]?.effects?.[0] || { item: it.id, prop: 'amount' };
  const into = target.item !== it.id ? g.byId[target.item] : null;
  const meta = into ? { unit: into.contents?.unit, label: `Pour into the ${into.name}` } : (it.amount || {});
  const current = (into ? g.ctx.state[into.id]?.[target.prop] : s.amount) ?? 0;
  const at = steps.findIndex((a) => a.value === current);
  const lower = at > 0 ? steps[at - 1] : at === -1 ? [...steps].reverse().find((a) => a.value < current) : null;
  const higher = at >= 0 && at < steps.length - 1 ? steps[at + 1] : at === -1 ? steps.find((a) => a.value > current) : null;
  const marks = steps.map((a) => a.value);
  const scale = { min: Math.min(0, ...marks), max: Math.max(...marks, 1) };
  const pct = (v) => levelPct(into ? scale : meta, v);
  return (
    <div className="mg-measure">
      <div className="mg-measure-tube" aria-label={`${current} ${meta.unit || ''}`}>
        {marks.map((v) => <span key={v} className="mg-measure-mark" style={{ bottom: `${pct(v)}%` }}>{v}</span>)}
        <i className="mg-measure-liquid" style={{ height: `${pct(current)}%` }} />
      </div>
      <div className="mg-closeup-side">
        <h4>{meta.label || it.name}</h4>
        {into && <p className="mg-measure-what">{it.name} in the {into.name}:</p>}
        <div className="mg-meter-value">{current}{meta.unit ? ` ${meta.unit}` : ''}</div>
        <div className="mg-measure-buttons">
          <Captioned caption={into ? 'Pour less' : 'Pour off'}>
            <button type="button" className="mg-step" disabled={!lower} onClick={() => lower && g.attempt(lower)}>−</button>
          </Captioned>
          <Captioned caption="Pour more">
            <button type="button" className="mg-step" disabled={!higher} onClick={() => higher && g.attempt(higher)}>+</button>
          </Captioned>
        </div>
        <Controls it={it} s={s} g={g} holds={g.actsFor(it.id, 'hold')} switches={g.actsFor(it.id, 'switch')} />
      </div>
    </div>
  );
}

/** A container's contents, part by part - and 🎒 Take it to hand it over. */
function ContentsCloseUp({ it, s, g }) {
  const parts = it.contents.ingredients.map((x) => ({ ...x, v: s[`contains_${x.id}`] || 0 }));
  const total = parts.reduce((n, x) => n + x.v, 0);
  return (
    <div className="mg-measure">
      <div className="mg-measure-tube" aria-label={`${total} ${it.contents.unit}`}>
        <i className="mg-measure-liquid" style={{ height: `${total ? levelPct({ min: 0, max: it.contents.max || total }, total) : 0}%` }} />
      </div>
      <div className="mg-closeup-side">
        <h4>{it.name}</h4>
        {total ? (
          <ul className="mg-contents">{parts.filter((x) => x.v).map((x) => <li key={x.id}><b>{x.v} {it.contents.unit}</b> {x.name}</li>)}</ul>
        ) : <p className="mg-measure-what">Empty.</p>}
        <Controls it={it} s={s} g={g} holds={g.actsFor(it.id, 'hold')} switches={g.actsFor(it.id, 'switch')} />
      </div>
    </div>
  );
}

/** An inventory item (backend inventory.py / flows.py): what it tells the player in its current state, and the
 * actions it offers there - each drawn by its mechanic: confirm = a button, choose = pick one option (the current
 * pick stays highlighted), amount = − / +. Every action of the item's state is always shown, so an item that's
 * part of the job can always be used. */
const fillRecords = (text, s) => String(text || '').replace(/\{(\w+)\}/g, (m, k) => (s[k] === null || s[k] === undefined ? '…' : String(s[k])));

const pct = (v, fallback = 0) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : fallback);

/** A camera monitor's live feed: the room as the camera sees it - the background and what's in it, as it is now
 * (boxes moved aside show moved aside), zoomed onto the items it films. No captions - the player looks and works
 * out what's wrong, then fixes it on the real thing. */
const minutes = (hhmm) => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim()); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const hhmm = (n) => `${String(Math.floor(n / 60) % 24).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
const startOf = (x) => x.inv?.start ?? Object.keys(x.inv?.states || {})[0];

function LiveFeed({ it, g, tag }) {
  const filmed = (it.inv.watches || []).map((id) => g.byId[id]).filter(Boolean);
  // Footage to scroll back through: before the time it appeared, what the camera films isn't there yet.
  const from = minutes(it.inv.timeline?.since);
  const appeared = minutes(it.inv.timeline?.appeared);
  const scrubbable = from !== null && appeared !== null && appeared > from;
  const end = scrubbable ? appeared + Math.max(10, Math.round((appeared - from) / 3)) : 0;
  const [at, setAt] = useState(end);
  const live = !scrubbable || at >= end;
  const notYet = (x) => scrubbable && at < appeared && filmed.includes(x) && (g.ctx.state[x.id] || {}).stage === startOf(x);
  const cx = filmed.length ? filmed.reduce((n, x) => n + pct(x.style?.left, 50) + pct(x.style?.width, 10) / 2, 0) / filmed.length : 50;
  const cy = filmed.length ? filmed.reduce((n, x) => n + pct(x.style?.bottom, 10) + 8, 0) / filmed.length : 40;
  const inRoom = g.spec.items.filter((x) => x.id !== it.id && !x.remote && x.kind !== 'place' && !(g.ctx.state[x.id] || {}).hidden && !notYet(x));
  return (
    <div className="mg-monitor mg-live-feed" role="img" aria-label={`Camera feed${tag ? `: ${tag}` : ''}`}>
      <div className="mg-live-room" style={{
        backgroundImage: g.spec.background_src ? `url(${g.asset(g.spec.background_src)})` : 'none',
        transform: filmed.length ? 'scale(2)' : 'none', transformOrigin: `${cx}% ${100 - cy}%`,
      }}>
        {inRoom.map((x) => {
          const xs = g.ctx.state[x.id] || {};
          const shifted = !!x.inv?.states?.[xs.stage]?.shift;
          return (
            <span key={x.id} className={`mg-live-thing ${shifted ? 'is-shifted' : ''}`} data-thing={x.id}
              style={{ left: x.style?.left, bottom: x.style?.bottom, width: x.style?.width }}>
              {x.src ? <img src={g.asset(x.src)} alt="" /> : <span className="mg-live-icon">{x.inv?.icon || '❔'}</span>}
            </span>
          );
        })}
      </div>
      <span className="mg-rec"><i>●</i> {live ? 'REC' : 'REPLAY'} {tag}</span>
      <span className="mg-stamp">{live ? g.monitorTime : hhmm(at)}</span>
      <span className="mg-scanlines" />
      {scrubbable && (
        <label className="mg-scrub">
          <span aria-hidden="true">⏪</span>
          <input type="range" min={from} max={end} step={1} value={at} aria-label="Scroll back through the footage"
            aria-valuetext={hhmm(at)} onChange={(e) => setAt(Number(e.target.value))} />
          <span>{live ? 'LIVE' : hhmm(at)}</span>
        </label>
      )}
    </div>
  );
}

/** An alarm panel's zones: one light per thing it guards - red while it's still open, green once it's secured. */
function Zones({ it, g }) {
  const guarded = (it.inv.watches || []).map((id) => g.byId[id]).filter(Boolean);
  if (!guarded.length) return null;
  return (
    <ul className="mg-zones" aria-label="Zones">
      {guarded.map((x, i) => {
        const open = (g.ctx.state[x.id] || {}).stage === startOf(x);
        return (
          <li key={x.id} className={open ? 'is-open' : 'is-secure'}>
            <i aria-hidden="true" /> Zone {i + 1} · {x.name} — {open ? 'open' : 'secure'}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Moving things (the generic core - backend flows.py "move" actions): take a collectible into the bag (from the scene
 * or out of a container), put one from the bag into a container (one of its places), use one on an item it combines
 * with. Shown on the item it happens at: a tray lists what's in it, the bins list their places and what you carry.
 */
// What a thing is called now: a thing made by a pick is what was picked (the wrong bottle taken is the wrong bottle).
export const shownName = (x, state) => {
  const from = x?.inv?.name_from;
  const picked = from && (state[from.item] || {})[from.prop];
  return picked ? (from.template || '{picked}').replace('{picked}', picked) : x?.name;
};

function Moves({ it, g }) {
  const moves = g.spec.actions.filter((a) => a.move && a.item === it.id);
  const where = (id) => (g.ctx.state[id] || {}).where;
  const thing = (id) => { const x = g.byId[id] || { name: id, inv: {} }; return { ...x, name: shownName(x, g.ctx.state) }; };
  const says = (id) => {
    const t = thing(id);
    const stage = (g.ctx.state[id] || {}).stage;
    return t.inv?.states?.[stage]?.conveys || '';
  };
  if (!moves.length) return null;
  const takes = moves.filter((a) => a.move.kind === 'take' && where(a.move.thing) === a.requires.value);
  const own = takes.find((a) => a.move.thing === it.id);              // this item itself, lying in the scene
  const puts = moves.filter((a) => a.move.kind === 'put' && where(a.move.thing) === 'bag');
  // (a tool used on it - "__use__" - is never offered here: which tool is the player's to work out, from the 🎒 tray)
  const combines = moves.filter((a) => a.move.kind === 'combine' && where(a.move.thing) === 'bag' && !a.id.includes('__use__'));
  const box = it.inv?.container;
  const places = box ? (box.slots.length ? box.slots.map((sl) => `${it.id}:${sl}`) : [it.id]) : [];
  // what this item has made and left on itself to take (the cup of coffee on the machine)
  const made = box ? [] : g.spec.items.filter((x) => x.inv?.made_by === it.id && where(x.id) === it.id);
  const person = it.kind === 'person';
  const inside = (place) => g.spec.items.filter((x) => where(x.id) === place);
  const take = (a) => { g.attempt(a); if (a.move.thing === it.id) g.setOpen(null); };
  const carriedHere = [...new Set(puts.map((a) => a.move.thing))];
  // too big to carry (a cart): moved straight from where it stands - the close-up shuts so the move is seen
  const shifts = it.inv?.carried === false
    ? moves.filter((a) => a.move.kind === 'put' && a.move.thing === it.id && where(it.id) !== a.move.to) : [];
  const placeName = (to) => { const [host, slot] = to.split(':'); return slot ? `${g.byId[host]?.name || host} - ${slot}` : g.byId[host]?.name || host; };
  return (
    <div className="mg-moves">
      {own && <button type="button" className="mg-btn" onClick={() => take(own)}>🎒 Take it</button>}
      {shifts.length > 0 && (
        <div className="mg-from-bag" aria-label="Move it">
          <span className="mg-inv-ask">Move it to:</span>
          {shifts.map((a) => (
            <button key={a.id} type="button" className="mg-btn mg-btn-sm" onClick={() => { if (g.attempt(a)) g.setOpen(null); }}>{placeName(a.move.to)}</button>
          ))}
        </div>
      )}
      {box && places.map((place) => (
        <div key={place} className="mg-place-box" aria-label={place.split(':')[1] || it.name}>
          {box.slots.length > 0 && <strong>{place.split(':')[1]}</strong>}
          {inside(place).length === 0 && <span className="mg-muted-note">{box.slots.length ? 'empty' : 'Nothing in it.'}</span>}
          {inside(place).map((x) => {
            const out = takes.find((a) => a.move.thing === x.id);
            return (
              <div key={x.id} className="mg-thing-row">
                <span className="mg-thing">{x.inv?.icon} <strong>{thing(x.id).name}</strong>{says(x.id) && <small> - {says(x.id)}</small>}</span>
                {out && <button type="button" className="mg-btn mg-btn-sm" onClick={() => take(out)}>Take</button>}
              </div>
            );
          })}
        </div>
      ))}
      {made.map((x) => {
        const out = takes.find((a) => a.move.thing === x.id);
        return (
          <div key={x.id} className="mg-thing-row mg-made" aria-label="Made here">
            <span className="mg-thing">{x.inv?.icon} <strong>{thing(x.id).name}</strong></span>
            {out && <button type="button" className="mg-btn mg-btn-sm" onClick={() => take(out)}>🎒 Take</button>}
          </div>
        );
      })}
      {carriedHere.length > 0 && (
        <div className="mg-from-bag" aria-label="From your bag">
          <span className="mg-inv-ask">{person ? `Give ${it.name}:` : 'From your bag:'}</span>
          {carriedHere.map((id) => (
            <div key={id} className="mg-thing-row">
              <span className="mg-thing">{thing(id).inv?.icon} {thing(id).name}</span>
              {puts.filter((a) => a.move.thing === id).map((a) => (
                <button key={a.id} type="button" className="mg-btn mg-btn-sm" onClick={() => g.attempt(a)}>
                  {box?.slots.length ? `Into ${a.move.to.split(':')[1]}` : person ? 'Give it' : 'Put it here'}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {combines.length > 0 && (
        <div className="mg-from-bag" aria-label="Use from your bag">
          {combines.map((a) => (
            <button key={a.id} type="button" className="mg-btn" onClick={() => g.attempt(a)}>
              {thing(a.move.thing).inv?.icon} Use the {thing(a.move.thing).name.toLowerCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const AMOUNTS = ['amount', 'pour', 'dial'];          // adjusted step by step - no single moment it's "done"
const ONCE = ['confirm', 'sign'];
const AUTO_CLOSE_MS = 1100;
const AUTO_CLOSE_VISUAL_MS = 1700;                 // a drawn item: let the movement (the door swinging shut) be seen
const AUTO_CLOSE_ADJUST_MS = 2500;

function InventoryCloseUp({ it, s, g }) {
  const inv = it.inv;
  const stage = inv.states[s.stage] || {};
  // Where an action can be done (the backend says: its state, states it's also offered in, where a move moved it);
  // missions saved before that: its state, or a move that can be redone once it's moved.
  const availableIn = (a, st) => (a.usable_in ? a.usable_in.includes(st) : a.state === st || (a.to === st && !ONCE.includes(a.mechanic)));
  const Visual = VISUALS[inv.visual];
  const simActions = (aid) => g.spec.actions.filter((a) => a.item === it.id && (a.id === `${it.id}__${aid}` || a.id.startsWith(`${it.id}__${aid}__`)));
  // only what this mission uses: an option it left unfilled (a first-aid kit with no list of things) has nothing to do
  const here = Object.entries(inv.actions).filter(([aid, a]) => availableIn(a, s.stage) && !(Visual?.handles || []).includes(aid)
    && simActions(aid).length > 0);
  const said = fillRecords(stage.conveys, s);
  const call = /: “/.test(said);                // someone speaking (the phone) - their words appear as spoken
  const feed = Array.isArray(inv.watches) && (inv.watch_view || 'feed') === 'feed';
  const pile = stage.pile || 0;
  // Once the player has given this item its input, it closes by itself - unless there's more to do on it now
  // (the phone picked up: now reply; the code typed: now arm).
  const closer = useRef(null);
  useEffect(() => () => clearTimeout(closer.current), []);
  const attemptOn = (aid, a) => (x) => {
    const ok = g.attempt(x);
    if (!ok) return ok;
    if (a.things) {
      // several things: close a while after the last one is placed (they may still change one) - not before
      const now = a.things.map((_, k) => (k === x.thing ? x.option : s[`${a.records}_${k}`]));
      clearTimeout(closer.current);
      if (now.every((v) => v !== null && v !== undefined)) closer.current = setTimeout(() => g.setOpen(null), AUTO_CLOSE_ADJUST_MS);
      return ok;
    }
    const after = ((x.effects || []).find((e) => e.item === it.id && e.prop === 'stage') || {}).value ?? s.stage;
    // more = another of this mission's steps on it that can be done now and isn't yet
    const more = Object.entries(inv.actions).some(([bid, b]) => bid !== aid && !b.undo && availableIn(b, after) && simActions(bid).length > 0
      && (b.records ? s[b.records] === null || s[b.records] === undefined : !simActions(bid).every((y) => g.ctx.done.includes(y.id))));
    clearTimeout(closer.current);
    if (!more) closer.current = setTimeout(() => g.setOpen(null),
      AMOUNTS.includes(a.mechanic) ? AUTO_CLOSE_ADJUST_MS : Visual ? AUTO_CLOSE_VISUAL_MS : AUTO_CLOSE_MS);
    return ok;
  };
  // for a drawn item: can its part be used now, and using it
  const can = (aid) => !!inv.actions[aid] && availableIn(inv.actions[aid], s.stage) && simActions(aid).length > 0;
  const act = (aid, option) => {           // option: which one, for a pick (the exit tapped on the plan)
    const x = option === undefined ? simActions(aid)[0] : simActions(aid).find((y) => y.option === option);
    return x ? attemptOn(aid, inv.actions[aid])(x) : false;
  };
  const pointing = here.some(([, a]) => a.mechanic === 'point');       // its picture is where you tap - shown once, big
  const shown = g.pictureOf ? g.pictureOf(it) : it.src;                // drawn per state: how it is now
  // an action that leads to a drawn state is shown as that picture (the new seat belt), not as words on a button
  const pictured = (a) => a.mechanic === 'confirm' && a.to && a.to !== s.stage && it.state_src?.[a.to];
  return (
    <div className={`mg-inv ${feed ? 'is-feed' : ''} ${Visual ? 'is-visual' : ''}`}>
      {Visual ? <Visual it={it} s={s} g={g} can={can} act={act} /> : feed ? <LiveFeed it={it} g={g} tag={said} /> : pointing ? null : (
        <div className="mg-inv-picture">
          {shown ? <img src={g.asset(shown)} alt={it.name} /> : <span className="mg-inv-icon">{inv.icon}</span>}
          {pile > 0 && (
            <div className="mg-pile" role="img" aria-label="What's on its shelves">
              {Array.from({ length: pile }, (_, i) => <span key={i}>{inv.pile_icon || '📦'}</span>)}
            </div>
          )}
        </div>
      )}
      <div className="mg-closeup-side mg-inv-side">
        <h4>{it.name}</h4>
        {inv.watch_view === 'zones' && !Visual && <Zones it={it} g={g} />}
        {said && !feed && !Visual && (inv.doc ? <Document doc={inv.doc} text={said} facts={g.spec.facts || []} />
          : call ? <Spoken text={said} facts={g.spec.facts || []} />
            : <p className="mg-inv-says"><Highlighted text={said} facts={g.spec.facts || []} /></p>)}
        <Moves it={it} g={g} />
        {here.some(([, a]) => pictured(a)) && (
          <div className="mg-pic-acts" aria-label="What you can do">
            {here.filter(([, a]) => pictured(a)).map(([aid, a]) => (
              <button key={aid} type="button" className={`mg-pic-act ${a.undo ? 'is-undo' : ''}`}
                onClick={() => { const x = simActions(aid)[0]; if (x) attemptOn(aid, a)(x); }}>
                <img src={g.asset(it.state_src[a.to])} alt="" />
                <span>{a.label}</span>
              </button>
            ))}
          </div>
        )}
        {here.filter(([, a]) => !pictured(a)).map(([aid, a]) => {
          const View = MECHANIC_VIEWS[a.mechanic] || MECHANIC_VIEWS.choose;
          return (
            <View key={aid} a={a} acts={simActions(aid)} value={a.records ? s[a.records] : undefined} attempt={attemptOn(aid, a)}
              values={a.things ? a.things.map((_, k) => s[`${a.records}_${k}`]) : undefined} text={inv.text || {}}
              src={shown} hotspots={inv.hotspots} marks={inv.marks}
              optionSrc={inv.option_src} asset={g.asset} icon={inv.icon} />
          );
        })}
      </div>
    </div>
  );
}

export const CLOSEUPS = createRegistry('close-up');
CLOSEUPS.register({ id: 'inventory', match: (it) => !!it.inv, View: InventoryCloseUp });
CLOSEUPS.register({ id: 'log', match: (it, s) => it.kind === 'record' || Array.isArray(s.entries), View: LogCloseUp });
CLOSEUPS.register({ id: 'monitor', match: (it) => it.kind === 'camera', View: MonitorCloseUp });
CLOSEUPS.register({ id: 'contents', match: (it) => !!it.contents, View: ContentsCloseUp });
CLOSEUPS.register({ id: 'measure', match: (it, s, g) => typeof s.amount === 'number' || (g && g.actsFor(it.id, 'adjust').length > 0), View: MeasureCloseUp });
CLOSEUPS.register({ id: 'plain', match: () => true, View: PlainCloseUp });
